<template>
  <v-sheet class="pa-2" color="surface-light" data-id-e2e="transfer-price-settings">
    <h3>
      {{ tkey('maintainOptionsLabel') }}
    </h3>
    <div class="d-flex mb-5 align-center">
      <v-select
        v-model="maintainValue"
        class="mr-2 d-flex flex-column"
        white
        :items="maintainOptions"
        :placeholder="$t('general.select')"
        hide-details
        width="200"
      />
      <v-btn
        class="d-flex flex-column"
        variant="flat"
        secondary
        :disabled="!maintainValue"
        @click="emitUpdateMaintainValues"
      >
        {{ tkey('applyPriceSetting') }}
      </v-btn>
    </div>
    <hr class="mb-3 separator" />
    <div class="input-container">
      <span>
        <b>{{ syncTypeInfo }} </b>
        {{ selectedPeriodIds }}
      </span>
      <v-btn
        primary
        class="sync-btn"
        :loading="saveInProgress"
        max-width="100"
        @click="emitSyncFinancials"
      >
        {{ $t('general.sync') }}
      </v-btn>
      <span>
        <b>{{ tkey('lastSynced') }}</b>
        {{ dayjs(dateSelections.lastRefreshed).format(getDateFormats.long) }}
      </span>
    </div>
  </v-sheet>
</template>

<script>
import { useDayjs } from '@/composables/use-dayjs';
import { useInternationalization } from '@/composables/use-internationalization';
import { useContextStore } from '@/stores/context';
import { useScenariosStore } from '@/stores/scenarios';
import { useWorkpackagesStore } from '@/stores/workpackages';
import maintainOptionsEnum from '@enums/price-change-maintain-option';
import { get, map, omit, size } from 'lodash';
import { mapState } from 'pinia';

export default {
  setup() {
    const { formatDateForSave, dayjs } = useDayjs();
    const { tkey } = useInternationalization('scenarioInputPage.attributes.options');

    return {
      formatDateForSave,
      dayjs,
      tkey,
    };
  },
  data() {
    return { maintainValue: null };
  },
  computed: {
    ...mapState(useContextStore, ['getDateFormats', 'getDefaultReverseFormat']),
    ...mapState(useScenariosStore, ['selectedScenario', 'saveInProgress']),
    ...mapState(useWorkpackagesStore, ['selectedWorkpackage']),

    selectedPeriodIds() {
      const selectedPeriodIds = get(this.selectedWorkpackage, 'periodIds', []);
      return selectedPeriodIds.join(', ');
    },

    syncTypeInfo() {
      const selectedPeriodIds = get(this.selectedWorkpackage, 'periodIds');
      return size(selectedPeriodIds) && !selectedPeriodIds.includes(null)
        ? this.tkey('selectedPeriodIds')
        : this.tkey('noneSelected');
    },

    dateSelections() {
      /**
       * The dates displayed will be in this order depending on what is available:
       *    - The value from the db
       *    - A year from the performance period start date
       */
      const transferPriceStartDate = this.formatDateForSave(
        get(
          this.selectedScenario,
          'transferPriceRange.transferPriceStartDate',
          this.dayjs(this.selectedWorkpackage.performanceStartDate).add(1, 'y')
        ),
        this.getDefaultReverseFormat
      );

      /**
       * The end dates displayed will be in this order depending on whats available:
       *    - The value from the db
       *    - A year from the performance period end date
       */
      const transferPriceEndDate = this.formatDateForSave(
        get(
          this.selectedScenario,
          'transferPriceRange.transferPriceEndDate',
          this.dayjs(this.selectedWorkpackage.performanceEndDate).add(1, 'y')
        ),
        this.getDefaultReverseFormat
      );

      const lastRefreshed = get(
        this.selectedScenario,
        'transferPriceRange.lastRefreshed',
        this.selectedScenario.creationDate
      );
      // Add the defaults to the workpackage
      return {
        transferPriceStartDate,
        transferPriceEndDate,
        lastRefreshed,
      };
    },

    maintainOptions() {
      // Allow all maintain options except for the 'manuallySet' option, as this should be a one-by-one decision of a user.
      const validMaintainOptions = omit(maintainOptionsEnum, 'manuallySet');
      return map(validMaintainOptions, (_, enumKey) => {
        return {
          title: this.$t(`scenarioInputPage.attributes.maintainOptions.${enumKey}`),
          value: enumKey,
        };
      });
    },
  },
  methods: {
    emitSyncFinancials() {
      // TODO: Remove dateSelections param when backend is updated to not require it. See https://owlabs.atlassian.net/browse/AOV3-8211
      // These dates are no longer used for syncing transfer prices, the period ID is used instead.
      // This code has been left here for now though because the backend still expects it and we can't change the
      // backend until Vue 2 is deprecated.
      this.$emit('sync-financials', this.dateSelections);
    },

    emitUpdateMaintainValues() {
      this.$emit('update-maintain', maintainOptionsEnum[this.maintainValue]);
    },
  },
};
</script>

<style lang="scss" scoped>
.input-container {
  display: flex;
  flex-direction: column;
}

h3 {
  margin-bottom: 0.5rem;
}

.sync-btn {
  margin-bottom: 0.5rem;
}
</style>
